import {
  AdgroupFormData,
  CampaignFormData,
  ACTION_TYPE,
  RejectInfo,
  AppProfilePageType,
  JUDGE_IDENTITY_PERMISSION_LIST,
  AcoFormData,
  FullNormalFormData,
  IDENTITY_AVAILABLE_STATUS,
  NormalCreative,
  UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP,
  CopyBannerErrorType,
  PRODUCT_SPECIFIC_TYPE,
  SpuInfo,
} from '@tt4b/ttam-enums';
import { Ref, ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api';
import {
  useAllowlist,
  useCreativeAuditInfo,
  useStdCreativeAdgroupIs,
  useCreativeIs,
  useCreativeAnchor,
  useCreativeAdgroupIs,
  useStore,
  NetStateEnum,
} from '@tt4b/ttam-hooks';
import { ProductListItemType } from '../../../components/creative/product-details/types';
import ProductDetails from '../../../components/creative/product-details/index';
import { i18n } from '@tt4b/ttam-i18n';
import { createSharedComposable } from '@vueuse/core';

export function useIos14AlertPropsVisible(props: { adgroupData: Ref<AdgroupFormData>; createMode: Ref<string> }) {
  const { adgroupData, createMode } = props;
  return {
    visible: computed(() => Boolean((adgroupData.value as any)?.isDrop) && createMode.value === ACTION_TYPE.EDIT),
  };
}

export function useRejectAlertPropsVisible(props: { rejectInfo: Ref<RejectInfo>; createMode: Ref<string> }) {
  const { createMode, rejectInfo } = props;

  const { isAppealing } = useCreativeAuditInfo({
    createMode,
    rejectInfo,
  });

  return {
    visible: isAppealing,
  };
}

export function useAppProfilePageTypeAlertPropsVisible(props: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = props;
  return {
    visible: computed(() => campaignData.value.campaignAppProfilePageType === AppProfilePageType.invalid),
  };
}

export function useSplittestAlertPropsVisible(props: { createMode: Ref<string> }) {
  const { createMode } = props;
  // TODO 待splittest支持
  const isSplitTestEnabled = ref(false);
  return {
    visible: computed(() => isSplitTestEnabled.value && createMode.value === ACTION_TYPE.CREATE),
  };
}

export function useIdentityAlertProps(props: {
  createMode: Ref<string>;
  creativeData: Ref<AcoFormData & FullNormalFormData>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  normalCreativeData: Ref<NormalCreative>;
}) {
  const { normalCreativeData, createMode, creativeData, adgroupData, campaignData } = props;

  const identityAvailableStatus = computed(() => creativeData.value?.identityData?.availableStatus || 0);

  const isShowIdentityPermission = computed(() =>
    JUDGE_IDENTITY_PERMISSION_LIST.includes(creativeData.value?.identityType || 0),
  );

  const { isCreativeHasIdentityWhite } = useAllowlist();
  const isEdit = computed(() => createMode.value === ACTION_TYPE.EDIT);

  const { isStandardAdsWithShop, isStandardAdsWithCatalog, isStandardAdsWithShowcase, isProductFromCatalog } =
    useStdCreativeAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

  // identity历史数据提示
  const isShowNoIdentityInfoWarning = computed(() => {
    if (!isCreativeHasIdentityWhite.value) {
      return false;
    }

    return isEdit.value && identityAvailableStatus.value === IDENTITY_AVAILABLE_STATUS.NOT_IDENTITY;
  });

  // identity账号权限不足时提示
  const identityWarningTitle = computed(
    () => UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()[identityAvailableStatus.value]?.bannerAlertDesc || '',
  );

  // 是否展示identity账号权限不足提示
  const { isNative } = useCreativeIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    createMode,
    normalCreativeData,
  });
  const isShowIdentityUnavailableWarning = computed(() => {
    if (
      !isNative.value ||
      isShowNoIdentityInfoWarning.value ||
      !isShowIdentityPermission.value ||
      !isCreativeHasIdentityWhite.value
    ) {
      return false;
    }

    if (identityAvailableStatus.value === IDENTITY_AVAILABLE_STATUS.Available) {
      return false;
    }

    return isEdit.value && identityWarningTitle.value;
  });

  return {
    unavailableMessage: identityWarningTitle,
    unavailableAlertType: computed(() => 'warning'),
    unavailableVisible: isShowIdentityUnavailableWarning,
    noInfoVisible: isShowNoIdentityInfoWarning,
    isStandardAdsWithShop,
    isStandardAdsWithCatalog,
    isStandardAdsWithShowcase,
    isProductFromCatalog,
  };
}

const copyBannerErrorMap = ref({});

// copy时顶部banner是否可见/可见时展示什么内容
export const useCreativeCopyAlert = createSharedComposable(function useCreativeCopyAlert() {
  // 文案统一整理
  const catalogErrorMsg = {
    NO_AUTH: i18n.t('module_creative_catalog_copy_no_permission'),
    ASSETS_LIMIT: i18n.t('module_creative_catalog_copy_assets_limit'),
    NO_PRODUCT: i18n.t('module_creative_catalog_copy_no_product'),
  };
  const shopErrorMsg = {
    NO_AUTH: i18n.t('module_creative_shop_copy_no_permission'),
    ASSETS_LIMIT: i18n.t('module_creative_shop_copy_assets_limit'),
    NO_TT: i18n.t('module_creative_shop_copy_not_bind_tt'),
    COUNTRY_NO_SUPPORT: i18n.t('module_creative_shop_copy_country_no_support'),
  };
  const identityErrorMsg = {
    NO_AUTH: 'identity is no auth',
    NOT_CUSTOM_AVAILABLE: 'custom identity is not available',
    NOT_TT_AVAILABLE: 'tt identity is not available',
  };
  const pixelErrorMsg = {
    CATALOG_NO_AUTH: i18n.t('module_ad_pixel_copy_unavailable'),
    SHOP_3P_NO_AUTH: i18n.t('module_ad_pixel_copy_unavailable'),
  };
  const liveAdsErrorMsg = {
    CANT_COPY: i18n.t('module_liveads_copy_limit'),
  };

  // 需求期望一类error能是顺序排列的并且权限错误应该在该类中第一个
  // copyBannerErrorMap 的结构应该是 Record < string, array > 的形式
  // const copyBannerErrorMap = ref({
  //   0: [catalogErrorMsg.NO_AUTH, catalogErrorMsg.ASSETS_LIMIT, catalogErrorMsg.NO_PRODUCT],
  //   1: [shopErrorMsg.NO_AUTH, shopErrorMsg.NO_TT],
  // });
  const setCopyBannerErrorMap = (key: CopyBannerErrorType, value: string) => {
    // 避免 setCopyBannerErrorMap(1,'') 等情况
    if (value) {
      if (!copyBannerErrorMap.value[key]) {
        copyBannerErrorMap.value = {
          ...copyBannerErrorMap.value,
          [key]: [],
        };
      }
      copyBannerErrorMap.value[key].push(value);
    }
  };
  const visible = computed(() => Object.keys(copyBannerErrorMap.value).length > 0);
  return {
    catalogErrorMsg,
    shopErrorMsg,
    identityErrorMsg,
    pixelErrorMsg,
    liveAdsErrorMsg,
    visible,
    copyBannerErrorMap,
    setCopyBannerErrorMap,
  };
});
export function useCreativeAnchorAlert(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  normalCreativeData: Ref<NormalCreative>;
}) {
  const { normalCreativeData, adgroupData, campaignData } = props;
  const { isShowAnchorAlert } = useCreativeAnchor({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    normalCreativeData,
  });
  return {
    visible: isShowAnchorAlert,
  };
}

export function useCreativeAllProductUnavailableAlert(props: {
  createMode: Ref<string>;
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  emit;
}) {
  const { createMode, campaignData: campaignFormData, adgroupData: adgroupFormData, creativeData, emit } = props;
  const { isProductFromShop, isPSA } = useCreativeAdgroupIs({
    createMode,
    campaignFormData,
    adgroupFormData,
  });
  const getIsAllProductUnavailable = (productList: Ref<ProductListItemType[]>) => {
    const unavailableProductList = productList.value.filter(product => product.disabled && product.id);
    const isAllProductUnavailable =
      unavailableProductList.length && unavailableProductList.length === productList.value.length;
    return isAllProductUnavailable;
  };
  const isEdit = computed(() => createMode.value === ACTION_TYPE.EDIT);
  const { productListDataSource } = ProductDetails.useProductListForShop({
    creativeData,
    emit,
  });

  const isDisabledAllSelectProduct = computed(() => {
    const { productSpecificType, spuDetailList = [] } = creativeData.value;
    if (isPSA.value && productSpecificType === PRODUCT_SPECIFIC_TYPE.ALL) {
      return spuDetailList.filter(item => !item.isDisabled).length === 0;
    }
    return getIsAllProductUnavailable(productListDataSource as Ref<ProductListItemType[]>);
  });

  const visible = computed(() => isEdit.value && isProductFromShop.value && isDisabledAllSelectProduct.value);
  return {
    visible,
  };
}

// 当用户没有shop or catalog的权限时，编辑ad层级，保存应该按钮置灰，页面顶部显示提示
export function useAccessShopOrCatalogEditAd(props: {
  createMode: Ref<string>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  canUseShowcase: Ref<boolean>;
}) {
  const { createMode, adgroupData, campaignData, canUseShowcase } = props;
  const {
    isStandardAdsWithShop,
    isStandardAdsWithCatalog,
    isStandardAdsWithShowcase,
    isProductSales,
    isProductFromCatalog,
  } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { state } = useStore();
  const isNotAccessShopOrCatalogEditAd = computed(() => {
    if (createMode.value !== ACTION_TYPE.EDIT || !isProductSales.value) {
      return false;
    }
    if (isProductFromCatalog.value) {
      const { data, hasLoaded } = state[NetStateEnum.catalogList];
      const { productPlatformId: catalogId, catalogAuthorizedBc } = adgroupData.value;
      return hasLoaded?.value && !data?.value?.find(v => v.platformId === catalogId && v.bc_id === catalogAuthorizedBc);
    } else if (isStandardAdsWithShop.value) {
      const { data, hasLoaded } = state[NetStateEnum.storeList];
      const { shopId, shopAuthorizedBc } = adgroupData.value;
      return hasLoaded?.value && !data?.value?.find(v => v.id === shopId && v.bc_id === shopAuthorizedBc);
    } else if (isStandardAdsWithShowcase.value) {
      return canUseShowcase.value === false;
    }
    return false;
  });

  return {
    isNotAccessShopOrCatalogEditAd,
  };
}

// 用户在已有计划下新建/复制创意，如果该用户没有计划上shop或者showcase的权限，展示提示
export function useAccessShopOrShowcaseCreateAd(props: {
  createMode: Ref<string>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  canUseShowcase: Ref<boolean>;
}) {
  const { createMode, adgroupData, campaignData, canUseShowcase } = props;
  const { isStandardAdsWithShop, isStandardAdsWithShowcase, isProductSales, isProductFromCatalog } =
    useStdCreativeAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
  const { state } = useStore();
  const isNotAccessShopOrShowcaseCreateAd = computed(() => {
    if (createMode.value !== ACTION_TYPE.CREATE || !isProductSales.value) {
      return false;
    }
    if (isProductFromCatalog.value && createMode.value === ACTION_TYPE.CREATE) {
      const { data, hasLoaded } = state[NetStateEnum.catalogList];
      const { productPlatformId: catalogId, catalogAuthorizedBc } = adgroupData.value;
      return hasLoaded?.value && !data?.value?.find(v => v.platformId === catalogId && v.bc_id === catalogAuthorizedBc);
    } else if (isStandardAdsWithShop.value && createMode.value === ACTION_TYPE.CREATE) {
      const { data, hasLoaded } = state[NetStateEnum.storeList];
      const { shopId, shopAuthorizedBc } = adgroupData.value;
      return hasLoaded?.value && !data?.value?.find(v => v.id === shopId && v.bc_id === shopAuthorizedBc);
    } else if (isStandardAdsWithShowcase.value) {
      return canUseShowcase.value === false;
    }
    return false;
  });
  return {
    isNotAccessShopOrShowcaseCreateAd,
  };
}
